import React, { useState } from 'react';
import { Table, Input } from 'antd';
import DeltaLine from '../../../../charts/DeltaLine';
import { stringMixin, money } from '../../../../utils/strings';
import { useTickerSearchIndex } from '../../../../modules/Search/useTickerSearchIndex';
import TickerChip from '../../../../components/TickerChip';

const { Search } = Input;

const HedgeFundTable = ({ portfolio }) => {
  // Compute dataSource with PUT/CALL delta percentage and sentiment
  const dataSource = Object.entries(portfolio)
    .map(([ticker, data], index) => {
      const totalOptions = data.calls + data.puts;
      const deltaPercentage = totalOptions > 0 ? ((data.calls - data.puts) / totalOptions) * 100 : 0; // Delta as a percentage
      const sentiment = data.shares + (data.calls - data.puts) > 0 ? 'Positive' : 'Negative'; // Sentiment calculation

      return {
        key: index,
        ticker,
        shares: data.shares,
        calls: data.calls,
        puts: data.puts,
        delta: deltaPercentage, // Delta percentage
        sentiment, // Sentiment
      };
    })
    .sort((a, b) => b.shares - a.shares); // Sort by total shares descending

  const uniqueTickerSymbols = dataSource.map((data) => data.ticker);
  const { tickers } = useTickerSearchIndex(uniqueTickerSymbols);

  // Get min and max deltas for normalization in DeltaLine
  const minDelta = Math.min(...dataSource.map((data) => data.delta));
  const maxDelta = Math.max(...dataSource.map((data) => data.delta));

  // State for search
  const [searchTerm, setSearchTerm] = useState('');
  const [pageSize, setPageSize] = useState(50);

  // Filter dataSource based on search term
  const filteredDataSource = dataSource.filter((data) =>
    data.ticker.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Define the table columns
  const columns = [
    {
      title: 'Ticker',
      dataIndex: 'ticker',
      key: 'ticker',
      render: (_, record) => {
        const ticker = tickers.find((ticker) => ticker.symbol === record.ticker);
        if (ticker) {
          return <TickerChip ticker={ticker} />;
        }
        return <span>{record.ticker}</span>;
      },
      width: 300,
      fixed: 'left'
    },
    {
      title: 'Shares',
      dataIndex: 'shares',
      key: 'shares',
      sorter: (a, b) => a.shares - b.shares,
      defaultSortOrder: 'descend',
      render: (shares) => `${money(shares, 0).slice(1)} (${stringMixin.largeNumber(shares)})`,
    },
    {
      title: 'Calls',
      dataIndex: 'calls',
      key: 'calls',
      sorter: (a, b) => a.calls - b.calls,
      render: (calls) => `${money(calls, 0).slice(1)} (${stringMixin.largeNumber(calls)})`,
    },
    {
      title: 'Puts',
      dataIndex: 'puts',
      key: 'puts',
      sorter: (a, b) => a.puts - b.puts,
      render: (puts) => `${money(puts, 0).slice(1)} (${stringMixin.largeNumber(puts)})`,
    },
    {
      title: 'Delta (%)',
      dataIndex: 'delta',
      key: 'delta',
      sorter: (a, b) => a.delta - b.delta,
      render: (delta) => (
        <span style={{ color: delta > 0 ? 'green' : 'red' }}>{delta.toFixed(2)}%</span>
      ),
    },
    {
      title: 'Delta Line',
      key: 'deltaLine',
      render: (_, record) => (
        <DeltaLine delta={record.delta} minDelta={minDelta} maxDelta={maxDelta} />
      ),
    },
    {
      title: 'Sentiment',
      dataIndex: 'sentiment',
      key: 'sentiment',
      render: (sentiment) => (
        <span style={{ color: sentiment === 'Positive' ? 'green' : 'red' }}>
          {sentiment}
        </span>
      ),
    },
  ];

  return (
    <div>
      <Search
        placeholder="Search by ticker"
        allowClear
        onChange={(e) => setSearchTerm(e.target.value)}
        style={{ marginBottom: '16px', width: '300px' }}
      />
      <Table
        dataSource={filteredDataSource}
        columns={columns}
        size={'middle'}
        className={'bg-transparent seethrough transparent'}
        pagination={{
          pageSize,
          pageSizeOptions: ['50', '100', '150', '200', '250'],
          showSizeChanger: true,
          onShowSizeChange: (_, size) => setPageSize(size),
        }}
        scroll={{
          y: '80vh',
        }}
      />
    </div>
  );
};

export default HedgeFundTable;
